import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
    Users as UsersIcon,
    X,
    Save,
    Search,
    Globe,
    DollarSign,
    Plus,
    Edit,
    Trash2,
    AlertTriangle,
    RefreshCw,
    Eye,
    EyeOff,
    LogIn,
    Link as LinkIcon
} from 'lucide-react';
import AdminLinkGeneratorModal from '../../components/admin/AdminLinkGeneratorModal';

interface User {
    id: string;
    email: string;
    role: 'admin' | 'affiliate';
    company_details: {
        name: string;
        address: string;
        city: string;
        postal_code: string;
        country: string;
    };
    country: string;
    vat_number?: string;
    overrides?: Record<string, { type: string; value: number }>;
    country_overrides?: Record<string, Record<string, { type: string; value: number }>>;
    event_overrides?: Record<string, Record<string, { type: string; value: number }>>;
    event_country_overrides?: Record<string, Record<string, Record<string, { type: string; value: number }>>>;
}

interface AffiliateApplication {
    id: string;
    name: string;
    email: string;
    telegram: string;
    trafficSource: string;
    estimatedVolume: string;
    geos: string;
    status: 'pending' | 'approved' | 'rejected';
    created_at: any;
}

interface Product {
    id: string;
    name: string;
}

export default function AdminUsers() {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [applications, setApplications] = useState<AffiliateApplication[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'affiliates' | 'applications'>('affiliates');
    const [searchTerm, setSearchTerm] = useState('');
    const [showOverrideModal, setShowOverrideModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUserForOverride, setSelectedUserForOverride] = useState<User | null>(null);
    const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null);
    const [selectedUserForDelete, setSelectedUserForDelete] = useState<User | null>(null);
    const [selectedUserForLink, setSelectedUserForLink] = useState<{ id: string; email: string; companyName?: string } | null>(null);
    const [showLinkGeneratorModal, setShowLinkGeneratorModal] = useState(false);
    const [overrideProduct, setOverrideProduct] = useState('');
    const [overrideRate, setOverrideRate] = useState('');
    const [overrideType, setOverrideType] = useState<'percentage' | 'fixed'>('percentage');
    const [overrideCountry, setOverrideCountry] = useState('');
    const [overrideEvent, setOverrideEvent] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const STANDARD_EVENTS = [
        'purchase', 'add_to_cart', 'click_link', 'view_content',
        'select_payment_method', 'payment_failed', 'upsell',
        'finished_quiz', 'started_quiz', 'clicked_lp'
    ];
    const canManageOverrides = hasPermission('manage_finance');

    // Form states for create/edit
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        company_name: '',
        country: '',
        vat_number: '',
        address: '',
        city: '',
        postal_code: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const usersQuery = query(collection(db, 'users'), where('role', '==', 'affiliate'));
            const usersSnapshot = await getDocs(usersQuery);
            const usersList = usersSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as User[];
            setUsers(usersList);

            const appsQuery = query(
                collection(db, 'affiliate_applications'),
                where('status', '==', 'pending'),
                orderBy('created_at', 'desc')
            );
            const appsSnapshot = await getDocs(appsQuery);
            const appsList = appsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as AffiliateApplication[];
            setApplications(appsList);

            const productsSnapshot = await getDocs(collection(db, 'products'));
            const productsList = productsSnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            })) as Product[];
            setProducts(productsList);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAffiliate = async () => {
        if (!formData.email || !formData.password || !formData.company_name) {
            alert('Email, password, and company name are required');
            return;
        }

        setActionLoading(true);
        try {
            const createFunc = httpsCallable(functions, 'createAffiliate');
            await createFunc(formData);
            alert('Affiliate created successfully!');
            setShowCreateModal(false);
            resetForm();
            fetchData();
        } catch (error: any) {
            console.error('Error creating affiliate:', error);
            alert(`Failed to create affiliate: ${error.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateAffiliate = async () => {
        if (!selectedUserForEdit) return;

        setActionLoading(true);
        try {
            const updateFunc = httpsCallable(functions, 'updateAffiliate');
            await updateFunc({
                user_id: selectedUserForEdit.id,
                ...formData
            });
            alert('Affiliate updated successfully!');
            setShowEditModal(false);
            setSelectedUserForEdit(null);
            resetForm();
            fetchData();
        } catch (error: any) {
            console.error('Error updating affiliate:', error);
            alert(`Failed to update affiliate: ${error.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteAffiliate = async () => {
        if (!selectedUserForDelete) return;

        setActionLoading(true);
        try {
            const deleteFunc = httpsCallable(functions, 'deleteAffiliate');
            await deleteFunc({ user_id: selectedUserForDelete.id });
            alert('Affiliate deleted successfully!');
            setShowDeleteModal(false);
            setSelectedUserForDelete(null);
            fetchData();
        } catch (error: any) {
            console.error('Error deleting affiliate:', error);
            alert(`Failed to delete affiliate: ${error.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleSetOverride = async () => {
        if (!canManageOverrides) {
            alert('You need the manage_finance permission to change commission overrides.');
            return;
        }

        if (!selectedUserForOverride || !overrideProduct || !overrideRate) {
            alert('Please fill in all fields');
            return;
        }

        setActionLoading(true);
        try {
            const updateOverrideFunc = httpsCallable(functions, 'updateUserOverride');
            await updateOverrideFunc({
                user_id: selectedUserForOverride.id,
                product_id: overrideProduct,
                event_type: overrideEvent || undefined,
                commission_type: overrideType,
                commission_rate: parseFloat(overrideRate),
                country: overrideCountry || undefined,
            });

            alert('Commission override set successfully!');
            setShowOverrideModal(false);
            setOverrideProduct('');
            setOverrideRate('');
            setOverrideType('percentage');
            setOverrideCountry('');
            setOverrideEvent('');
            fetchData();
        } catch (error: any) {
            console.error('Error setting override:', error);
            const message = error?.code === 'functions/permission-denied'
                ? 'You need the manage_finance permission to change commission overrides.'
                : `Failed to set override: ${error?.message || 'Unknown error'}`;
            alert(message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemoveOverride = async (userId: string, productId: string, country?: string, eventName?: string) => {
        if (!canManageOverrides) {
            alert('You need the manage_finance permission to change commission overrides.');
            return;
        }

        if (!confirm('Remove this commission override?')) return;

        try {
            const updateOverrideFunc = httpsCallable(functions, 'updateUserOverride');
            await updateOverrideFunc({
                user_id: userId,
                product_id: productId,
                country: country || undefined,
                event_type: eventName || undefined,
                remove: true,
            });
            fetchData();
        } catch (error: any) {
            console.error('Error removing override:', error);
            const message = error?.code === 'functions/permission-denied'
                ? 'You need the manage_finance permission to change commission overrides.'
                : `Failed to remove override: ${error?.message || 'Unknown error'}`;
            alert(message);
        }
    };

    const handleLoginAs = async (user: User) => {
        if (!confirm(`Log in as ${user.email}? You will be automatically redirected.`)) return;

        setActionLoading(true);
        try {
            const getLoginToken = httpsCallable<{ target_uid: string }, { success: boolean, token: string }>(functions, 'generateLoginToken');
            const result = await getLoginToken({ target_uid: user.id });

            if (result.data.success && result.data.token) {
                await signInWithCustomToken(auth, result.data.token);
                // Auth state observer will update role and React Router will do the rest
                navigate('/');
            } else {
                throw new Error("Invalid token received");
            }
        } catch (error: any) {
            console.error('Error logging in as affiliate:', error);
            alert(`Failed to log in as affiliate: ${error.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleApproveApplication = async (applicationId: string) => {
        if (!confirm('Are you sure you want to approve this affiliate application? An account will be created and welcome email sent.')) return;

        setActionLoading(true);
        try {
            const approveFunc = httpsCallable(functions, 'approveAffiliateApplication');
            await approveFunc({ application_id: applicationId });
            alert('Application approved successfully!');
            fetchData();
        } catch (error: any) {
            console.error('Error approving application:', error);
            alert(`Failed to approve application: ${error.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectApplication = async (applicationId: string) => {
        const reason = prompt('Optional: Enter a rejection reason (internal only)');
        if (reason === null) return; // User cancelled prompt

        setActionLoading(true);
        try {
            const rejectFunc = httpsCallable(functions, 'rejectAffiliateApplication');
            await rejectFunc({ application_id: applicationId, reason });
            alert('Application rejected.');
            fetchData();
        } catch (error: any) {
            console.error('Error rejecting application:', error);
            alert(`Failed to reject application: ${error.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const openEditModal = (user: User) => {
        setSelectedUserForEdit(user);
        setFormData({
            email: user.email,
            password: '',
            company_name: user.company_details?.name || '',
            country: user.country || '',
            vat_number: user.vat_number || '',
            address: user.company_details?.address || '',
            city: user.company_details?.city || '',
            postal_code: user.company_details?.postal_code || ''
        });
        setShowEditModal(true);
    };

    const resetForm = () => {
        setFormData({
            email: '',
            password: '',
            company_name: '',
            country: '',
            vat_number: '',
            address: '',
            city: '',
            postal_code: ''
        });
        setShowPassword(false);
    };

    const generatePassword = () => {
        const length = 16;
        const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
        let password = '';
        for (let i = 0; i < length; i++) {
            password += charset.charAt(Math.floor(Math.random() * charset.length));
        }
        setFormData({ ...formData, password });
        setShowPassword(true);
    };

    const filteredUsers = users.filter(user => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            user.email.toLowerCase().includes(term) ||
            user.company_details?.name?.toLowerCase().includes(term)
        );
    });

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-2">
                <div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight glow-text">Affiliate Management</h1>
                    <p className="text-slate-400 mt-1 font-medium">Manage partnerships and custom commission rates</p>
                </div>
                {activeTab === 'affiliates' && (
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="btn-primary shrink-0"
                    >
                        <Plus size={20} className="mr-2" />
                        <span>Create New Affiliate</span>
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-white/10 mb-6">
                <button
                    onClick={() => setActiveTab('affiliates')}
                    className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${activeTab === 'affiliates'
                        ? 'border-primary-500 text-primary-400'
                        : 'border-transparent text-gray-400 hover:text-white hover:border-white/20'
                        }`}
                >
                    Active Affiliates ({users.length})
                </button>
                <button
                    onClick={() => setActiveTab('applications')}
                    className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'applications'
                        ? 'border-primary-500 text-primary-400'
                        : 'border-transparent text-gray-400 hover:text-white hover:border-white/20'
                        }`}
                >
                    Pending Applications
                    {applications.length > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {applications.length}
                        </span>
                    )}
                </button>
            </div>

            {activeTab === 'affiliates' ? (
                <>
                    {/* Search & Filters */}
                    <div className="relative group mb-6">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Search className="text-slate-500 group-focus-within:text-primary-400 transition-colors" size={20} />
                        </div>
                        <input
                            type="text"
                            placeholder="Search by email, company name or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input-field pl-12 h-14 text-lg bg-white/5 border-white/10 focus:bg-white/10 shadow-lg"
                        />
                    </div>

                    {/* Users List */}
                    <div className="card">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : filteredUsers.length === 0 ? (
                            <div className="text-center py-12">
                                <UsersIcon className="mx-auto text-gray-500 mb-4" size={48} />
                                <h3 className="text-lg font-semibold text-white mb-2">No Affiliates Found</h3>
                                <p className="text-gray-400">No affiliates match your search.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {filteredUsers.map((user) => (
                                    <div key={user.id} className="p-4 bg-white/5 rounded-xl border border-white/5">
                                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                            {/* User Info */}
                                            <div className="flex items-center gap-5">
                                                <div className="w-14 h-14 rounded-2xl bg-linear-to-br from-primary-500 to-accent-600 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                                                    <span className="text-white font-bold text-xl uppercase">
                                                        {user.company_details?.name?.charAt(0) || user.email.charAt(0)}
                                                    </span>
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-white text-lg leading-none mb-1">{user.company_details?.name || 'No Company Name'}</h3>
                                                    <p className="text-sm text-slate-400 font-medium mb-2">{user.email}</p>
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/5 border border-white/5">
                                                            <Globe size={12} className="text-primary-400" />
                                                            <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">{user.country || 'Global'}</span>
                                                        </div>
                                                        {user.vat_number && (
                                                            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/5 border border-white/5">
                                                                <span className="text-[11px] font-bold text-slate-400">VAT: {user.vat_number}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-2">
                                                {canManageOverrides && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedUserForOverride(user);
                                                            setShowOverrideModal(true);
                                                        }}
                                                        className="btn-secondary py-2 px-4 text-sm flex items-center gap-2"
                                                    >
                                                        <DollarSign size={16} />
                                                        Override
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        setSelectedUserForLink({
                                                            id: user.id,
                                                            email: user.email,
                                                            companyName: user.company_details?.name
                                                        });
                                                        setShowLinkGeneratorModal(true);
                                                    }}
                                                    className="btn-secondary py-2 px-4 text-sm flex items-center gap-2"
                                                >
                                                    <LinkIcon size={16} />
                                                    Link
                                                </button>
                                                <button
                                                    onClick={() => handleLoginAs(user)}
                                                    className="btn-secondary py-2 px-4 text-sm flex items-center gap-2"
                                                    disabled={actionLoading}
                                                >
                                                    <LogIn size={16} />
                                                    Login As
                                                </button>
                                                <button
                                                    onClick={() => openEditModal(user)}
                                                    className="btn-secondary py-2 px-4 text-sm flex items-center gap-2"
                                                >
                                                    <Edit size={16} />
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedUserForDelete(user);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    className="btn-secondary py-2 px-4 text-sm flex items-center gap-2 text-red-400 hover:text-red-300"
                                                >
                                                    <Trash2 size={16} />
                                                    Delete
                                                </button>
                                            </div>
                                        </div>

                                        {/* Existing Overrides */}
                                        {((user.overrides && Object.keys(user.overrides).length > 0) || (user.event_overrides && Object.keys(user.event_overrides).length > 0)) && (
                                            <div className="mt-4 pt-4 border-t border-white/10">
                                                <h4 className="text-sm font-medium text-gray-400 mb-2">Commission Overrides</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {/* Legacy Product Overrides */}
                                                    {Object.entries(user.overrides || {}).map(([productId, override]) => {
                                                        const product = products.find(p => p.id === productId);
                                                        return (
                                                            <div
                                                                key={`legacy-${productId}`}
                                                                className="flex items-center gap-2 px-3 py-1 bg-primary-500/20 rounded-full"
                                                            >
                                                                <span className="text-sm text-primary-300">
                                                                    {product?.name || productId}: {override.value}{override.type === 'percentage' ? '%' : ' fixed'}
                                                                </span>
                                                                {canManageOverrides && (
                                                                    <button
                                                                        onClick={() => handleRemoveOverride(user.id, productId)}
                                                                        className="text-primary-400 hover:text-red-400"
                                                                    >
                                                                        <X size={14} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}

                                                    {/* Event Product Overrides */}
                                                    {Object.entries(user.event_overrides || {}).map(([productId, eventsMap]) => {
                                                        const product = products.find(p => p.id === productId);
                                                        return Object.entries(eventsMap).map(([eventName, override]) => (
                                                            <div
                                                                key={`event-${productId}-${eventName}`}
                                                                className="flex items-center gap-2 px-3 py-1 bg-emerald-500/20 rounded-full"
                                                            >
                                                                <span className="text-sm text-emerald-300">
                                                                    {product?.name || productId} ({eventName}): {override.value}{override.type === 'percentage' ? '%' : ' fixed'}
                                                                </span>
                                                                {canManageOverrides && (
                                                                    <button
                                                                        onClick={() => handleRemoveOverride(user.id, productId, undefined, eventName)}
                                                                        className="text-emerald-400 hover:text-red-400"
                                                                    >
                                                                        <X size={14} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ));
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="card">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : applications.length === 0 ? (
                        <div className="text-center py-12">
                            <UsersIcon className="mx-auto text-gray-500 mb-4" size={48} />
                            <h3 className="text-lg font-semibold text-white mb-2">No Pending Applications</h3>
                            <p className="text-gray-400">There are currently no affiliate applications waiting for review.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {applications.map((app) => (
                                <div key={app.id} className="p-5 bg-white/5 rounded-xl border border-white/5 flex flex-col xl:flex-row gap-6">
                                    <div className="flex-1 space-y-4">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h3 className="font-bold text-white text-xl">{app.name}</h3>
                                                <p className="text-sm text-slate-400">{app.email}</p>
                                            </div>
                                            <span className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                                                Pending Review
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-black/20 rounded-lg">
                                            <div>
                                                <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">Telegram</p>
                                                <p className="text-sm text-white font-medium">{app.telegram || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">Traffic Source</p>
                                                <p className="text-sm text-white font-medium">{app.trafficSource}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">Vol / Month</p>
                                                <p className="text-sm text-white font-medium">{app.estimatedVolume || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">Target GEOs</p>
                                                <p className="text-sm text-white font-medium">{app.geos || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex xl:flex-col gap-3 justify-center border-t xl:border-t-0 xl:border-l border-white/10 pt-4 xl:pt-0 xl:pl-6">
                                        <button
                                            onClick={() => handleApproveApplication(app.id)}
                                            disabled={actionLoading}
                                            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2.5 px-6 rounded-lg transition-colors flex-1 xl:flex-none"
                                        >
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => handleRejectApplication(app.id)}
                                            disabled={actionLoading}
                                            className="bg-slate-700 hover:bg-red-600 text-white font-bold py-2.5 px-6 rounded-lg transition-colors flex-1 xl:flex-none"
                                        >
                                            Reject
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Create Affiliate Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="glass-dark rounded-2xl p-6 w-full max-w-2xl animate-slide-up max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-white">Create New Affiliate</h2>
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    resetForm();
                                }}
                                className="text-gray-400 hover:text-white"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="input-field"
                                        placeholder="affiliate@example.com"
                                        required
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Password *
                                    </label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={formData.password}
                                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                className="input-field pr-10"
                                                placeholder="••••••••"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={generatePassword}
                                            className="btn-secondary px-4 flex items-center gap-2 whitespace-nowrap"
                                        >
                                            <RefreshCw size={16} />
                                            Generate
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        A welcome email with password setup link will be sent to the affiliate
                                    </p>
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Company Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.company_name}
                                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                        className="input-field"
                                        placeholder="Company Inc."
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Country
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.country}
                                        onChange={(e) => setFormData({ ...formData, country: e.target.value.toUpperCase() })}
                                        className="input-field"
                                        placeholder="US"
                                        maxLength={2}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        VAT Number
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.vat_number}
                                        onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                                        className="input-field"
                                        placeholder="US123456789"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Address
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        className="input-field"
                                        placeholder="123 Main St"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.city}
                                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                        className="input-field"
                                        placeholder="New York"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Postal Code
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.postal_code}
                                        onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                                        className="input-field"
                                        placeholder="10001"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreateAffiliate}
                                    disabled={actionLoading}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                                >
                                    {actionLoading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Save size={18} />
                                            Create Affiliate
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Affiliate Modal */}
            {showEditModal && selectedUserForEdit && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="glass-dark rounded-2xl p-6 w-full max-w-2xl animate-slide-up max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-white">Edit Affiliate</h2>
                            <button
                                onClick={() => {
                                    setShowEditModal(false);
                                    setSelectedUserForEdit(null);
                                    resetForm();
                                }}
                                className="text-gray-400 hover:text-white"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                <p className="text-sm text-blue-300">
                                    Editing: <strong>{selectedUserForEdit.email}</strong>
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="input-field"
                                        placeholder="affiliate@example.com"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        New Password (Optional)
                                    </label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={formData.password}
                                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                className="input-field pr-10"
                                                placeholder="Leave blank to keep current"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={generatePassword}
                                            className="btn-secondary px-4 flex items-center gap-2 whitespace-nowrap"
                                        >
                                            <RefreshCw size={16} />
                                            Generate
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Only fill this if you want to manually change the affiliate's password.
                                    </p>
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Company Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.company_name}
                                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                        className="input-field"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Country
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.country}
                                        onChange={(e) => setFormData({ ...formData, country: e.target.value.toUpperCase() })}
                                        className="input-field"
                                        maxLength={2}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        VAT Number
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.vat_number}
                                        onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                                        className="input-field"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Address
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        className="input-field"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.city}
                                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                        className="input-field"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Postal Code
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.postal_code}
                                        onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                                        className="input-field"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setSelectedUserForEdit(null);
                                        resetForm();
                                    }}
                                    className="btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateAffiliate}
                                    disabled={actionLoading}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                                >
                                    {actionLoading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Save size={18} />
                                            Update Affiliate
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedUserForDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="glass-dark rounded-2xl p-6 w-full max-w-md animate-slide-up">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                                    <AlertTriangle className="text-red-400" size={24} />
                                </div>
                                <h2 className="text-xl font-bold text-white">Delete Affiliate</h2>
                            </div>
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedUserForDelete(null);
                                }}
                                className="text-gray-400 hover:text-white"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <p className="text-gray-300">
                                Are you sure you want to delete this affiliate? This action cannot be undone.
                            </p>

                            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                                <p className="text-sm text-red-300">
                                    <strong>Email:</strong> {selectedUserForDelete.email}<br />
                                    <strong>Company:</strong> {selectedUserForDelete.company_details?.name || 'N/A'}
                                </p>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setSelectedUserForDelete(null);
                                    }}
                                    className="btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteAffiliate}
                                    disabled={actionLoading}
                                    className="bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex-1 flex items-center justify-center gap-2"
                                >
                                    {actionLoading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Trash2 size={18} />
                                            Delete Affiliate
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Override Modal */}
            {canManageOverrides && showOverrideModal && selectedUserForOverride && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="glass-dark rounded-2xl p-6 w-full max-w-md animate-slide-up">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-white">Set Commission Override</h2>
                            <button
                                onClick={() => setShowOverrideModal(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-gray-400 mb-2">Setting override for:</p>
                                <p className="text-white font-medium">
                                    {selectedUserForOverride.company_details?.name || selectedUserForOverride.email}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Product
                                </label>
                                <select
                                    value={overrideProduct}
                                    onChange={(e) => setOverrideProduct(e.target.value)}
                                    className="input-field"
                                >
                                    <option value="">Select a product...</option>
                                    {products.map(product => (
                                        <option key={product.id} value={product.id}>
                                            {product.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Commission Type
                                </label>
                                <select
                                    value={overrideType}
                                    onChange={(e) => setOverrideType(e.target.value as 'percentage' | 'fixed')}
                                    className="input-field"
                                >
                                    <option value="percentage">Percentage (%)</option>
                                    <option value="fixed">Fixed Amount ($)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Commission {overrideType === 'percentage' ? 'Rate (%)' : 'Amount ($)'}
                                </label>
                                <input
                                    type="number"
                                    value={overrideRate}
                                    onChange={(e) => setOverrideRate(e.target.value)}
                                    placeholder={overrideType === 'percentage' ? 'e.g., 25' : 'e.g., 5.00'}
                                    min="0"
                                    max={overrideType === 'percentage' ? '100' : undefined}
                                    step={overrideType === 'percentage' ? '0.1' : '0.01'}
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Country (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={overrideCountry}
                                    onChange={(e) => setOverrideCountry(e.target.value.toUpperCase())}
                                    placeholder="e.g., DE, FR (leave empty for all)"
                                    maxLength={2}
                                    className="input-field"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    If specified, this rate only applies to customers from this country
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Event Type (Optional)
                                </label>
                                <select
                                    value={overrideEvent}
                                    onChange={(e) => setOverrideEvent(e.target.value)}
                                    className="input-field"
                                >
                                    <option value="">Any Base Sale</option>
                                    {STANDARD_EVENTS.map(evt => (
                                        <option key={evt} value={evt}>{evt}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Use if commission only applies to a certain conversion event step.
                                </p>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => setShowOverrideModal(false)}
                                    className="btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSetOverride}
                                    disabled={actionLoading}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                                >
                                    {actionLoading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Save size={18} />
                                            Save Override
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <AdminLinkGeneratorModal
                isOpen={showLinkGeneratorModal}
                onClose={() => {
                    setShowLinkGeneratorModal(false);
                    setSelectedUserForLink(null);
                }}
                affiliate={selectedUserForLink}
            />
        </div>
    );
}
